import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { VERSION } from '../version';
import './Landing.css';

function Landing() {
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error
  const features = [
    {
      icon: '👥',
      title: 'CRM completo de clientes',
      description: 'Centraliza fichas, historial, etiquetas, interacciones, documentos y seguimiento en una sola vista para trabajar con contexto completo en cada sesión.'
    },
    {
      icon: '📅',
      title: 'Agenda visual intuitiva',
      description: 'Programa citas presenciales y online, controla disponibilidad, envía recordatorios automáticos y gestiona videollamadas desde el propio CRM.'
    },
    {
      icon: '📈',
      title: 'Histórico avanzado y KPIs',
      description: 'Analiza tu evolución con métricas y gráficos por rango de fechas, filtros predefinidos, persistencia de filtros y comparativas para tomar mejores decisiones.'
    },
    {
      icon: '💼',
      title: 'Pipeline de oportunidades',
      description: 'Gestiona oportunidades por etapas, valor estimado y probabilidad, conecta presupuestos y facturas, y visualiza tu pipeline comercial en tiempo real.'
    },
    {
      icon: '🧾',
      title: 'Facturación profesional de extremo a extremo',
      description: 'Crea facturas, presupuestos, recurrentes, pagos, descuentos y exportaciones con dashboard financiero, alertas de cobro y seguimiento del estado de cada documento.'
    },
    {
      icon: '💱',
      title: 'Moneda configurable por usuario',
      description: 'Define moneda de facturación y posición del símbolo (antes o después del importe). Todo el sistema muestra importes de forma coherente en todas las pantallas.'
    },
    {
      icon: '🛡️',
      title: 'Backups administrables desde panel',
      description: 'Como administrador puedes generar, descargar y gestionar copias de seguridad de base de datos desde la interfaz, con compatibilidad para hostings con permisos restringidos.'
    },
    {
      icon: '🔎',
      title: 'Búsqueda global inteligente',
      description: 'Encuentra en segundos clientes, citas, sesiones, facturas y datos relevantes sin navegar entre módulos.'
    },
    {
      icon: '🌐',
      title: 'Portal del cliente y branding',
      description: 'Comparte portal con logo y colores personalizados para que cada cliente consulte citas, facturas y archivos en una experiencia profesional y coherente con tu marca.'
    }
  ];

  const benefits = [
    {
      icon: '⚡',
      title: 'Ahorra tiempo y aumenta tu productividad',
      description: 'Automatiza tareas operativas y reduce la carga administrativa para dedicar más tiempo al trabajo de alto valor con tus clientes.'
    },
    {
      icon: '💰',
      title: 'Mejor control de ingresos y cobros',
      description: 'Visualiza facturado, cobrado, pendiente y vencido en dashboards claros para mejorar cashflow y anticipar acciones comerciales.'
    },
    {
      icon: '⭐',
      title: 'Profesionaliza tu práctica',
      description: 'Ofrece una experiencia premium con documentos, facturas, portal cliente, comunicaciones y branding personalizados.'
    },
    {
      icon: '📈',
      title: 'Escala con decisiones basadas en datos',
      description: 'Con histórico, reportes y pipeline comercial detectas oportunidades de mejora y haces crecer tu negocio con más confianza.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-content">
          <img 
            src="/coachflow-logo.png" 
            alt="COACHFLOW Logo" 
            className="hero-logo"
          />
          <p className="hero-subtitle">
            La plataforma todo-en-uno para coaches y consultores
          </p>
          <p className="hero-description">
            Gestiona CRM, agenda, facturación, oportunidades, portal cliente y backups desde un solo lugar. Profesionaliza tu operación y escala con datos reales.
          </p>
          <div className="hero-cta">
            <a href="/login.php" className="btn btn-primary btn-large">
              Comenzar Ahora
            </a>
            <a href="#features" className="btn btn-secondary btn-large">
              Conocer Más
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-features">
        <div className="container">
          <h2 className="section-title">Todo lo que necesitas en un solo lugar</h2>
          <p className="section-subtitle">
            Funcionalidades conectadas para operar tu negocio de forma profesional, segura y escalable
          </p>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="landing-benefits">
        <div className="container">
          <h2 className="section-title">Beneficios reales para tu negocio</h2>
          <p className="section-subtitle">
            Descubre cómo COACHFLOW puede transformar tus horas prácticas profesionales
          </p>
          <div className="benefits-list">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                <div className="benefit-icon">{benefit.icon}</div>
                <div className="benefit-content">
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="landing-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">70%</div>
              <div className="stat-label">Menos tiempo administrativo</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Datos seguros </div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Acceso desde cualquier lugar</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Clientes y sesiones ilimitadas</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="container">
          <h2 className="cta-title">¿Listo para transformar tus horas prácticas?</h2>
          <p className="cta-description">
            Únete a profesionales que ya usan COACHFLOW para centralizar clientes, ventas, facturación y operación diaria.
            Comienza hoy mismo y experimenta la diferencia.
          </p>
          <div className="cta-buttons">
            <a href="/login.php" className="btn btn-primary btn-large">
              Acceder Ahora
            </a>
            <a href="#features" className="btn btn-outline btn-large">
              Ver Funcionalidades
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contacto" className="landing-contact">
        <div className="container">
          <h2 className="section-title">Contacta con nosotros</h2>
          <p className="section-subtitle">
            ¿Tienes preguntas o quieres saber más? Escríbenos y te responderemos lo antes posible.
          </p>

          {formStatus === 'success' ? (
            <div className="contact-success">
              <div className="contact-success-icon">✅</div>
              <h3>¡Gracias por tu mensaje!</h3>
              <p>Hemos recibido tu consulta correctamente. Te responderemos lo antes posible.</p>
              <button
                className="btn btn-secondary"
                onClick={() => setFormStatus('idle')}
                style={{ marginTop: '16px' }}
              >
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <form
              className="contact-form"
              onSubmit={async (e) => {
                e.preventDefault();
                setFormStatus('sending');
                try {
                  const formData = new FormData(e.target);
                  const res = await fetch('https://formspree.io/f/maqdykbk', {
                    method: 'POST',
                    body: formData,
                    headers: { Accept: 'application/json' },
                  });
                  if (res.ok) {
                    setFormStatus('success');
                    e.target.reset();
                  } else {
                    setFormStatus('error');
                  }
                } catch {
                  setFormStatus('error');
                }
              }}
            >
              <div className="contact-form-grid">
                <div className="form-group">
                  <label htmlFor="contact-name">Nombre *</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="nombre"
                    required
                    placeholder="Tu nombre"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-email">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="tu@email.com"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="contact-subject">Asunto</label>
                <input
                  id="contact-subject"
                  type="text"
                  name="asunto"
                  placeholder="¿En qué podemos ayudarte?"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-message">Mensaje *</label>
                <textarea
                  id="contact-message"
                  name="mensaje"
                  required
                  rows="5"
                  placeholder="Escribe tu mensaje aquí..."
                />
              </div>
              {formStatus === 'error' && (
                <div style={{ color: '#e74c3c', marginBottom: '12px', fontSize: '14px' }}>
                  Ha ocurrido un error al enviar el mensaje. Inténtalo de nuevo.
                </div>
              )}
              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={formStatus === 'sending'}
                style={{ width: '100%' }}
              >
                {formStatus === 'sending' ? 'Enviando...' : 'Enviar mensaje'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="app-footer">
        <p>
          &copy; 2025-26 CdL. All rights reserved. | Made with Love in Spain | v{VERSION}
          <span className="footer-links">
            {' | '}<Link to="/privacidad" style={{ color: 'inherit', opacity: 0.8 }}>Política de Privacidad</Link>
            {' | '}<Link to="/terminos" style={{ color: 'inherit', opacity: 0.8 }}>Términos y Condiciones</Link>
          </span>
        </p>
      </footer>
    </div>
  );
}

export default Landing;

